import { createModuleLogger } from './logger';

// Logger para el gestor de memoria
const memoryLogger = createModuleLogger('PDFMemoryManager');

/**
 * Gestor centralizado de memoria para PDFs
 * Controla el número máximo de PDFs en memoria y gestiona su cleanup automático
 */
class PDFMemoryManager {
  constructor() {
    // Configuración por defecto
    this.config = {
      maxPDFs: 3,           // Máximo 3 PDFs en memoria simultáneamente
      maxSizeBytes: 50 * 1024 * 1024, // Máximo 50MB total en memoria
      cleanupDelay: 30000,  // 30 segundos antes de limpiar PDFs inactivos
    };
    
    // Tracking de PDFs activos
    this.activePDFs = new Map(); // Key: pdfId, Value: { url, blob, size, timestamp, isActive }
    this.cleanupTimers = new Map(); // Key: pdfId, Value: timer
    
    // Estadísticas
    this.stats = {
      totalCreated: 0,
      totalCleaned: 0,
      currentCount: 0,
      currentSizeBytes: 0,
      peakCount: 0,
      peakSizeBytes: 0
    };
    
    // Bind methods para preservar contexto
    this.registerPDF = this.registerPDF.bind(this);
    this.unregisterPDF = this.unregisterPDF.bind(this);
    this.markAsActive = this.markAsActive.bind(this);
    this.markAsInactive = this.markAsInactive.bind(this);
    
    memoryLogger.info('PDFMemoryManager inicializado', { config: this.config });
  }
  
  /**
   * Registra un nuevo PDF en el sistema
   * @param {string} pdfId - ID único del PDF
   * @param {string} url - Blob URL del PDF
   * @param {Blob} blob - Blob object
   * @returns {boolean} - true si se registró exitosamente
   */
  registerPDF(pdfId, url, blob) {
    try {
      const size = blob?.size || 0;
      
      // Verificar límites antes de registrar
      if (!this._canRegisterPDF(size)) {
        this._enforceMemoryLimits();
        
        // Verificar nuevamente después del cleanup
        if (!this._canRegisterPDF(size)) {
          memoryLogger.warn('No se puede registrar PDF - límites excedidos', {
            pdfId,
            requestedSize: size,
            currentCount: this.stats.currentCount,
            currentSize: this.stats.currentSizeBytes,
            maxPDFs: this.config.maxPDFs,
            maxSize: this.config.maxSizeBytes
          });
          return false;
        }
      }
      
      // Registrar el PDF
      const pdfInfo = {
        url,
        blob,
        size,
        timestamp: Date.now(),
        isActive: true,
        registeredAt: new Date().toISOString()
      };
      
      this.activePDFs.set(pdfId, pdfInfo);
      this._updateStats(size, 1);
      
      memoryLogger.debug('PDF registrado', {
        pdfId,
        size,
        totalPDFs: this.stats.currentCount,
        totalSize: this.stats.currentSizeBytes
      });
      
      return true;
    } catch (error) {
      memoryLogger.error('Error al registrar PDF', { pdfId, error });
      return false;
    }
  }
  
  /**
   * Desregistra un PDF del sistema y limpia su URL
   * @param {string} pdfId - ID del PDF a desregistrar
   * @returns {boolean} - true si se desregistró exitosamente
   */
  unregisterPDF(pdfId) {
    try {
      const pdfInfo = this.activePDFs.get(pdfId);
      
      if (!pdfInfo) {
        memoryLogger.debug('PDF no encontrado para desregistrar', { pdfId });
        return false;
      }
      
      // Limpiar blob URL
      this._cleanupBlobUrl(pdfInfo.url, `unregister_${pdfId}`);
      
      // Limpiar timer si existe
      this._clearCleanupTimer(pdfId);
      
      // Remover del tracking
      this.activePDFs.delete(pdfId);
      this._updateStats(-pdfInfo.size, -1);
      this.stats.totalCleaned++;
      
      memoryLogger.debug('PDF desregistrado', {
        pdfId,
        size: pdfInfo.size,
        totalPDFs: this.stats.currentCount,
        totalSize: this.stats.currentSizeBytes
      });
      
      return true;
    } catch (error) {
      memoryLogger.error('Error al desregistrar PDF', { pdfId, error });
      return false;
    }
  }
  
  /**
   * Marca un PDF como activo (en uso)
   * @param {string} pdfId - ID del PDF
   */
  markAsActive(pdfId) {
    const pdfInfo = this.activePDFs.get(pdfId);
    if (pdfInfo) {
      pdfInfo.isActive = true;
      pdfInfo.timestamp = Date.now();
      
      // Cancelar timer de cleanup si existe
      this._clearCleanupTimer(pdfId);
      
      memoryLogger.debug('PDF marcado como activo', { pdfId });
    }
  }
  
  /**
   * Marca un PDF como inactivo y programa su cleanup
   * @param {string} pdfId - ID del PDF
   */
  markAsInactive(pdfId) {
    const pdfInfo = this.activePDFs.get(pdfId);
    if (pdfInfo) {
      pdfInfo.isActive = false;
      pdfInfo.timestamp = Date.now();
      
      // Programar cleanup después del delay
      this._scheduleCleanup(pdfId);
      
      memoryLogger.debug('PDF marcado como inactivo', { pdfId });
    }
  }
  
  /**
   * Obtiene estadísticas del gestor de memoria
   * @returns {Object} - Estadísticas actuales
   */
  getStats() {
    return {
      ...this.stats,
      activePDFs: Array.from(this.activePDFs.keys()),
      memoryUsagePercent: (this.stats.currentSizeBytes / this.config.maxSizeBytes) * 100,
      pdfUsagePercent: (this.stats.currentCount / this.config.maxPDFs) * 100
    };
  }
  
  /**
   * Limpia todos los PDFs de memoria (para uso en casos extremos)
   */
  clearAll() {
    memoryLogger.info('Limpiando todos los PDFs de memoria', {
      count: this.stats.currentCount,
      size: this.stats.currentSizeBytes
    });
    
    for (const [pdfId] of this.activePDFs) {
      this.unregisterPDF(pdfId);
    }
  }
  
  /**
   * Configura los límites del gestor
   * @param {Object} newConfig - Nueva configuración
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    memoryLogger.info('Configuración actualizada', { config: this.config });
    
    // Verificar límites con nueva configuración
    this._enforceMemoryLimits();
  }
  
  // Métodos privados
  
  /**
   * Verifica si se puede registrar un nuevo PDF
   * @param {number} size - Tamaño del PDF en bytes
   * @returns {boolean}
   */
  _canRegisterPDF(size) {
    const wouldExceedCount = this.stats.currentCount >= this.config.maxPDFs;
    const wouldExceedSize = (this.stats.currentSizeBytes + size) > this.config.maxSizeBytes;
    
    return !wouldExceedCount && !wouldExceedSize;
  }
  
  /**
   * Enforza los límites de memoria removiendo PDFs antiguos/inactivos
   */
  _enforceMemoryLimits() {
    // Obtener PDFs ordenados por prioridad de remoción
    const pdfsByPriority = this._getPDFsByRemovalPriority();
    
    for (const [pdfId, pdfInfo] of pdfsByPriority) {
      // No remover PDFs activos a menos que sea absolutamente necesario
      if (pdfInfo.isActive && this._hasInactivePDFs()) {
        continue;
      }
      
      // Verificar si aún necesitamos hacer cleanup
      const withinLimits = this.stats.currentCount < this.config.maxPDFs && 
                          this.stats.currentSizeBytes < this.config.maxSizeBytes;
      
      if (withinLimits) {
        break;
      }
      
      memoryLogger.info('Removiendo PDF para liberar memoria', {
        pdfId,
        isActive: pdfInfo.isActive,
        age: Date.now() - pdfInfo.timestamp,
        size: pdfInfo.size
      });
      
      this.unregisterPDF(pdfId);
    }
  }
  
  /**
   * Obtiene PDFs ordenados por prioridad de remoción
   * @returns {Array} - Array de [pdfId, pdfInfo] ordenado por prioridad
   */
  _getPDFsByRemovalPriority() {
    return Array.from(this.activePDFs.entries()).sort(([, a], [, b]) => {
      // Prioridad: inactivos primero, luego por edad (más antiguos primero)
      if (a.isActive !== b.isActive) {
        return a.isActive ? 1 : -1; // Inactivos primero
      }
      return a.timestamp - b.timestamp; // Más antiguos primero
    });
  }
  
  /**
   * Verifica si hay PDFs inactivos
   * @returns {boolean}
   */
  _hasInactivePDFs() {
    return Array.from(this.activePDFs.values()).some(pdf => !pdf.isActive);
  }
  
  /**
   * Programa el cleanup de un PDF inactivo
   * @param {string} pdfId - ID del PDF
   */
  _scheduleCleanup(pdfId) {
    // Cancelar timer existente
    this._clearCleanupTimer(pdfId);
    
    // Programar nuevo cleanup
    const timer = setTimeout(() => {
      const pdfInfo = this.activePDFs.get(pdfId);
      if (pdfInfo && !pdfInfo.isActive) {
        memoryLogger.debug('Cleanup automático de PDF inactivo', { pdfId });
        this.unregisterPDF(pdfId);
      }
    }, this.config.cleanupDelay);
    
    this.cleanupTimers.set(pdfId, timer);
  }
  
  /**
   * Cancela el timer de cleanup de un PDF
   * @param {string} pdfId - ID del PDF
   */
  _clearCleanupTimer(pdfId) {
    const timer = this.cleanupTimers.get(pdfId);
    if (timer) {
      clearTimeout(timer);
      this.cleanupTimers.delete(pdfId);
    }
  }
  
  /**
   * Limpia una blob URL de manera segura
   * @param {string} url - URL a limpiar
   * @param {string} context - Contexto para logging
   */
  _cleanupBlobUrl(url, context) {
    if (url && typeof URL !== 'undefined' && URL.revokeObjectURL) {
      try {
        URL.revokeObjectURL(url);
        memoryLogger.debug(`Blob URL limpiada - contexto: ${context}`, { url });
      } catch (error) {
        memoryLogger.error('Error al limpiar blob URL', { url, context, error });
      }
    }
  }
  
  /**
   * Actualiza las estadísticas
   * @param {number} sizeChange - Cambio en bytes
   * @param {number} countChange - Cambio en cantidad
   */
  _updateStats(sizeChange, countChange) {
    this.stats.currentSizeBytes += sizeChange;
    this.stats.currentCount += countChange;
    
    if (countChange > 0) {
      this.stats.totalCreated++;
    }
    
    // Actualizar picos
    this.stats.peakCount = Math.max(this.stats.peakCount, this.stats.currentCount);
    this.stats.peakSizeBytes = Math.max(this.stats.peakSizeBytes, this.stats.currentSizeBytes);
  }
}

// Instancia singleton del gestor
const pdfMemoryManager = new PDFMemoryManager();

// Event listeners globales para cleanup en casos extremos
if (typeof window !== 'undefined') {
  // Cleanup en beforeunload
  window.addEventListener('beforeunload', () => {
    memoryLogger.info('beforeunload detectado - limpiando memoria PDF');
    pdfMemoryManager.clearAll();
  }, { passive: true });
  
  // Cleanup en visibilitychange cuando la página se oculta
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      memoryLogger.debug('Página oculta - marcando PDFs como inactivos');
      // Marcar todos los PDFs como inactivos para acelerar cleanup
      const stats = pdfMemoryManager.getStats();
      stats.activePDFs.forEach(pdfId => {
        pdfMemoryManager.markAsInactive(pdfId);
      });
    }
  }, { passive: true });
}

export default pdfMemoryManager;
